import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../../../utils/echarts'
import type { TFunction } from 'i18next'
import type { DemographicTrend } from '../../demographicsService'

interface PopulationTrendChartProps {
  data: DemographicTrend[]
  height?: number
  t: TFunction
}

export interface PopulationTrendChartRef {
  exportChart: () => string | null
}

export const PopulationTrendChart = forwardRef<
  PopulationTrendChartRef,
  PopulationTrendChartProps
>(({ data, height = 400, t }, ref) => {
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<echarts.ECharts | null>(null)

  useImperativeHandle(ref, () => ({
    exportChart: () => {
      if (chartInstance.current) {
        return chartInstance.current.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff',
        })
      }
      return null
    },
  }))

  useEffect(() => {
    if (!chartRef.current || !data || data.length === 0) return

    // Initialize chart
    chartInstance.current = safeChartInit(chartRef.current)

    // Sort data by year
    const sortedData = [...data].sort((a, b) => a.year - b.year)

    // Prepare data arrays
    const years = sortedData.map((d) => d.year.toString())
    const totalPopulation = sortedData.map((d) => d.total_population)
    const swissPopulation = sortedData.map((d) => d.total_population_swiss)
    const foreignPopulation = sortedData.map((d) => d.total_population_foreign)
    const households = sortedData.map((d) => d.total_households_number)

    // Calculate growth rates for annotations (reserved for future use)
    const _growthRates = totalPopulation.map((current, index) => {
      if (index === 0) return 0
      const previous = totalPopulation[index - 1]
      return previous > 0 ? ((current - previous) / previous) * 100 : 0
    })

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis',
        confine: true,
        axisPointer: {
          type: 'cross',
        },
        formatter: function (params: any) {
          const year = params[0].axisValue
          const totalLabel = t('charts.populationTrend.legend.totalPopulation')
          const swissLabel = t('charts.populationTrend.legend.swissPopulation')
          const foreignLabel = t(
            'charts.populationTrend.legend.foreignPopulation'
          )
          const householdsLabel = t('charts.populationTrend.legend.households')
          const total =
            params.find((p: any) => p.seriesName === totalLabel)?.value || 0
          const swiss =
            params.find((p: any) => p.seriesName === swissLabel)?.value || 0
          const foreign =
            params.find((p: any) => p.seriesName === foreignLabel)?.value || 0
          const householdsVal =
            params.find((p: any) => p.seriesName === householdsLabel)?.value ||
            0

          const foreignPercentage =
            total > 0 ? ((foreign / total) * 100).toFixed(1) : '0'
          const avgHouseholdSize =
            householdsVal > 0 ? (total / householdsVal).toFixed(1) : '0'

          return `
            <div style="text-align: left;">
              <strong>${t('charts.populationTrend.tooltips.year')}: ${year}</strong><br/>
              <span style="color: #3b82f6;">● ${t('charts.populationTrend.tooltips.total')}: ${total.toLocaleString()}</span><br/>
              <span style="color: #10b981;">● ${t('charts.populationTrend.tooltips.swiss')}: ${swiss.toLocaleString()}</span><br/>
              <span style="color: #f59e0b;">● ${t('charts.populationTrend.tooltips.foreign')}: ${foreign.toLocaleString()} (${foreignPercentage}%)</span><br/>
              <span style="color: #8b5cf6;">● ${t('charts.populationTrend.tooltips.households')}: ${householdsVal.toLocaleString()}</span><br/>
              <span style="color: #6b7280;">● ${t('charts.populationTrend.tooltips.avgSize')}: ${avgHouseholdSize}</span>
            </div>
          `
        },
      },
      legend: {
        data: [
          t('charts.populationTrend.legend.totalPopulation'),
          t('charts.populationTrend.legend.swissPopulation'),
          t('charts.populationTrend.legend.foreignPopulation'),
          t('charts.populationTrend.legend.households'),
        ],
        top: 10,
        type: 'scroll',
        textStyle: {
          fontSize: 11,
        },
        itemGap: 16,
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: years,
        axisLabel: {
          fontSize: 11,
          rotate: years.length > 8 ? 45 : 0,
          color: '#1f2937',
        },
        axisLine: {
          lineStyle: {
            color: '#e5e7eb',
          },
        },
      },
      yAxis: [
        {
          type: 'value',
          name: t('charts.populationTrend.axis.population'),
          position: 'left',
          axisLabel: {
            fontSize: 11,
            formatter: function (value: number) {
              if (value >= 1000) {
                return (value / 1000).toFixed(1) + 'k'
              }
              return value.toString()
            },
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
          splitLine: {
            lineStyle: {
              color: '#f3f4f6',
              type: 'dashed',
            },
          },
        },
        {
          type: 'value',
          name: t('charts.populationTrend.axis.households'),
          position: 'right',
          axisLabel: {
            fontSize: 11,
            formatter: function (value: number) {
              if (value >= 1000) {
                return (value / 1000).toFixed(1) + 'k'
              }
              return value.toString()
            },
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
          splitLine: {
            show: false,
          },
        },
      ],
      series: [
        {
          name: t('charts.populationTrend.legend.totalPopulation'),
          type: 'line',
          data: totalPopulation,
          lineStyle: {
            color: '#3b82f6',
            width: 3,
          },
          itemStyle: {
            color: '#3b82f6',
          },
          symbol: 'circle',
          symbolSize: 6,
          emphasis: {
            itemStyle: {
              borderColor: '#3b82f6',
              borderWidth: 2,
            },
          },
        },
        {
          name: t('charts.populationTrend.legend.swissPopulation'),
          type: 'line',
          data: swissPopulation,
          lineStyle: {
            color: '#10b981',
            width: 2,
          },
          itemStyle: {
            color: '#10b981',
          },
          symbol: 'circle',
          symbolSize: 4,
        },
        {
          name: t('charts.populationTrend.legend.foreignPopulation'),
          type: 'line',
          data: foreignPopulation,
          lineStyle: {
            color: '#f59e0b',
            width: 2,
          },
          itemStyle: {
            color: '#f59e0b',
          },
          symbol: 'circle',
          symbolSize: 4,
        },
        {
          name: t('charts.populationTrend.legend.households'),
          type: 'line',
          yAxisIndex: 1,
          data: households,
          lineStyle: {
            color: '#8b5cf6',
            width: 2,
            type: 'dashed',
          },
          itemStyle: {
            color: '#8b5cf6',
          },
          symbol: 'triangle',
          symbolSize: 5,
        },
      ],
    }

    chartInstance.current.setOption(option)

    // Handle resize
    const handleResize = () => {
      chartInstance.current?.resize()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      chartInstance.current?.dispose()
    }
  }, [data, height])

  if (!data || data.length === 0) {
    return (
      <div
        style={{
          height: `${height}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#6b7280',
        }}
      >
        {t('charts.populationTrend.noData')}
      </div>
    )
  }

  return (
    <div
      ref={chartRef}
      style={{
        width: '100%',
        height: `${height}px`,
        minHeight: '300px',
      }}
    />
  )
})

PopulationTrendChart.displayName = 'PopulationTrendChart'
